import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import logoIcon from "../assets/images/logo-icon.svg";
import { registerUser, verifyEmailCode, resendVerificationCode, setInitialPassword } from "../auth";
import { useAuth } from "../contexts/Auth";
import { claimPendingScores } from "../courses";
import OtpInput from "../components/OtpInput";

const RESEND_COOLDOWN = 30; // seconds, mirrors the backend throttle
const TEACHER_CONTACT_EMAIL = "kontakt@aussprachetrainer.org";

const Register = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { login } = useAuth();

    // step: "form" → role + email, "verify" → 6-digit code, "password" → choose password
    const [step, setStep] = useState("form");

    const [role, setRole] = useState(null); // "learner" | "teacher"
    const [teacherCode, setTeacherCode] = useState("");
    const [teacherCodeError, setTeacherCodeError] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [emailError, setEmailError] = useState("");
    const [formError, setFormError] = useState("");
    const [roleError, setRoleError] = useState(false);
    const [passwordTooShort, setPasswordTooShort] = useState(false);
    const [emailExistsError, setEmailExistsError] = useState(false);
    const [submitButtonIsDisabled, setSubmitButtonIsDisabled] = useState(false);
    const [settingPassword, setSettingPassword] = useState(false);

    // verification step state
    const [code, setCode] = useState("");
    const [codeError, setCodeError] = useState(false);
    const [codeErrorMsg, setCodeErrorMsg] = useState("");
    const [verifying, setVerifying] = useState(false);
    const [verified, setVerified] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendNotice, setResendNotice] = useState("");
    const cooldownRef = useRef(null);
    // Course to redirect to after signup when the visitor came from a shared
    // course link and we claimed their anonymous practice scores.
    const claimedCourseIdRef = useRef(null);

    const handleLanguageChange = (e) => {
        i18n.changeLanguage(e.target.value);
    };

    const location = useLocation();
    const signup_button_source = location.state?.signup_source || "direct";

    // Update error messages when language changes
    useEffect(() => {
        if (passwordTooShort) {
            setPasswordError(t("auth.passwordTooShort"));
        } else {
            setPasswordError("");
        }
        if (emailExistsError) {
            setFormError(
                <span>
                    {t("auth.emailAlreadyExists")}{" "}
                    <Link to="/login" className="text-purple-500 hover:underline">
                        {t("auth.loginInstead")}
                    </Link>
                </span>
            );
        }
    }, [i18n.language, passwordTooShort, emailExistsError, t]);

    // resend cooldown ticker
    const startCooldown = () => {
        setResendCooldown(RESEND_COOLDOWN);
        clearInterval(cooldownRef.current);
        cooldownRef.current = setInterval(() => {
            setResendCooldown((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => () => clearInterval(cooldownRef.current), []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setEmailError("");
        setFormError("");
        setRoleError(false);
        setTeacherCodeError("");
        setEmailExistsError(false);

        if (!role) {
            setRoleError(true);
            return;
        }

        // Teachers need a beta code (validity is checked server-side).
        if (role === "teacher" && !teacherCode.trim()) {
            setTeacherCodeError(t("auth.teacherCodeRequired"));
            return;
        }

        setSubmitButtonIsDisabled(true);

        try {
            const urlParams = new URLSearchParams(window.location.search);
            const referrer = urlParams.get("ref") || null;

            await registerUser(email, {
                role,
                ...(role === "teacher" && { teacher_code: teacherCode.trim() }),
                referrer,
                signup_button_source,
                language: i18n.language,
            });

            // Move to the verification step
            setStep("verify");
            startCooldown();
        } catch (error) {
            setSubmitButtonIsDisabled(false);
            if (error.fieldErrors) {
                if (error.fieldErrors.teacher_code) {
                    setTeacherCodeError(t("auth.teacherCodeInvalid"));
                }
                if (error.fieldErrors.email) {
                    const emailErr = error.fieldErrors.email[0];
                    if (emailErr.includes("already exists")) {
                        setEmailExistsError(true);
                        setFormError(
                            <span>
                                {t("auth.emailAlreadyExists")}{" "}
                                <a href="/login" className="text-purple-500 hover:underline">
                                    {t("auth.loginInstead")}
                                </a>
                            </span>
                        );
                    } else {
                        setEmailError(emailErr);
                    }
                }
                if (error.fieldErrors.non_field_errors) {
                    setFormError(error.fieldErrors.non_field_errors[0]);
                }
            } else {
                setFormError(error.message || "Registration failed");
            }
        }
    };

    const handleVerify = async (codeToCheck) => {
        const finalCode = codeToCheck || code;
        if (finalCode.length !== 6 || verifying) return;

        setVerifying(true);
        setCodeError(false);
        setCodeErrorMsg("");

        try {
            const data = await verifyEmailCode(email, finalCode);
            setVerified(true);
            // Log in with the tokens, then move on to choosing a password.
            login(data.access, data.refresh);
            // Persist any anonymous practice scores collected from a shared
            // course link before signup (no-op when there are none).
            claimedCourseIdRef.current = await claimPendingScores();
            setTimeout(() => {
                setStep("password");
            }, 900);
        } catch (error) {
            setVerifying(false);
            setCodeError(true);
            setCode("");
            if (error.code === "expired") {
                setCodeErrorMsg(t("auth.codeExpired"));
            } else if (error.code === "too_many_attempts" || error.status === 429) {
                setCodeErrorMsg(t("auth.codeTooManyAttempts"));
            } else if (typeof error.attemptsRemaining === "number") {
                setCodeErrorMsg(
                    t("auth.codeIncorrectWithAttempts", { count: error.attemptsRemaining })
                );
            } else {
                setCodeErrorMsg(t("auth.codeIncorrect"));
            }
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        setResendNotice("");
        setCodeError(false);
        setCodeErrorMsg("");
        try {
            await resendVerificationCode(email, i18n.language);
            setResendNotice(t("auth.codeResent"));
            startCooldown();
        } catch (error) {
            setResendNotice(t("auth.codeResendFailed"));
        }
    };

    const handleSetPassword = async (e) => {
        e.preventDefault();
        setPasswordError("");
        setPasswordTooShort(false);

        if (password.length < 8) {
            setPasswordTooShort(true);
            setPasswordError(t("auth.passwordTooShort"));
            return;
        }

        setSettingPassword(true);
        try {
            await setInitialPassword(password);
            const courseId = claimedCourseIdRef.current;
            navigate(courseId ? `/courses/${courseId}` : "/dashboard", { replace: true });
        } catch (error) {
            setSettingPassword(false);
            if (error.fieldErrors?.new_password) {
                setPasswordError(error.fieldErrors.new_password[0]);
            } else {
                setPasswordError(error.message || t("auth.passwordTooShort"));
            }
        }
    };

    const roleOptions = [
        {
            id: "learner",
            title: t("auth.roleLearnerTitle"),
            subtitle: t("auth.roleLearnerSubtitle"),
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.6}
                    className="h-7 w-7"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25"
                    />
                </svg>
            ),
        },
        {
            id: "teacher",
            title: t("auth.roleTeacherTitle"),
            subtitle: t("auth.roleTeacherSubtitle"),
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1.6}
                    className="h-7 w-7"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
                    />
                </svg>
            ),
        },
    ];

    const CloseButton = () => (
        <button
            type="button"
            onClick={() => navigate("/")}
            aria-label={t("auth.close")}
            title={t("auth.close")}
            className="absolute top-4 left-4 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1.5 transition-colors"
        >
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="h-5 w-5"
            >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
        </button>
    );

    const LanguageSwitcher = () => (
        <div className="absolute top-4 right-4 flex items-center gap-2">
            <span
                className={`cursor-pointer ${
                    i18n.language === "en" ? "font-bold" : ""
                } hover-underline underline-black`}
                onClick={() => handleLanguageChange({ target: { value: "en" } })}
            >
                EN
            </span>
            <span>|</span>
            <span
                className={`cursor-pointer ${
                    i18n.language === "de" ? "font-bold" : ""
                } hover-underline underline-black`}
                onClick={() => handleLanguageChange({ target: { value: "de" } })}
            >
                DE
            </span>
        </div>
    );

    // ── Verification step ────────────────────────────────────────────
    if (step === "verify") {
        return (
            <div className="flex justify-center bg-white pt-8 sm:pt-16 px-2 sm:p-2">
                <div className="w-full max-w-md space-y-5 sm:space-y-6 p-4 sm:p-8 border border-gray-200 rounded-xl shadow-sm relative step-fade-in">
                    <CloseButton />
                    <LanguageSwitcher />
                    <div className="flex justify-center">
                        <img
                            src={logoIcon}
                            alt="AusspracheTrainer Logo"
                            className="h-20 w-20"
                        />
                    </div>

                    <div className="text-center space-y-1">
                        <h2 className="text-2xl text-gray-900">{t("auth.verifyTitle")}</h2>
                        <p className="text-sm text-gray-500">
                            {t("auth.verifySubtitle")}{" "}
                            <span className="font-medium text-gray-700">{email}</span>
                        </p>
                    </div>

                    {verified ? (
                        <div className="flex flex-col items-center py-4 space-y-3">
                            <div className="otp-success flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                    className="h-8 w-8 text-success"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="m4.5 12.75 6 6 9-13.5"
                                    />
                                </svg>
                            </div>
                            <p className="text-sm font-medium text-gray-700">
                                {t("auth.verifiedSuccess")}
                            </p>
                        </div>
                    ) : (
                        <>
                            <OtpInput
                                value={code}
                                onChange={(c) => {
                                    setCode(c);
                                    if (codeError) {
                                        setCodeError(false);
                                        setCodeErrorMsg("");
                                    }
                                }}
                                onComplete={handleVerify}
                                error={codeError}
                                disabled={verifying}
                            />

                            {codeErrorMsg && (
                                <p className="text-center text-sm text-error">{codeErrorMsg}</p>
                            )}
                            {resendNotice && !codeErrorMsg && (
                                <p className="text-center text-sm text-success">{resendNotice}</p>
                            )}

                            <button
                                type="button"
                                disabled={code.length !== 6 || verifying}
                                onClick={() => handleVerify()}
                                className="w-full bg-primary hover:bg-purple-500 text-white py-2.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {verifying && (
                                    <svg
                                        className="animate-spin h-4 w-4 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"
                                        />
                                    </svg>
                                )}
                                {t("auth.verifyButton")}
                            </button>

                            <div className="text-center text-sm text-gray-500">
                                {t("auth.didntGetCode")}{" "}
                                {resendCooldown > 0 ? (
                                    <span className="text-gray-400">
                                        {t("auth.resendIn", { seconds: resendCooldown })}
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleResend}
                                        className="text-primary hover:underline font-medium"
                                    >
                                        {t("auth.resendCode")}
                                    </button>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={() => {
                                    setStep("form");
                                    setCode("");
                                    setCodeError(false);
                                    setCodeErrorMsg("");
                                    setSubmitButtonIsDisabled(false);
                                }}
                                className="w-full text-center text-sm text-gray-400 hover:text-gray-600"
                            >
                                {t("auth.changeEmail")}
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ── Password step (after verification) ───────────────────────────
    if (step === "password") {
        return (
            <div className="flex justify-center bg-white pt-8 sm:pt-16 px-2 sm:p-2">
                <div className="w-full max-w-md space-y-5 sm:space-y-6 p-4 sm:p-8 border border-gray-200 rounded-xl shadow-sm relative step-fade-in">
                    <CloseButton />
                    <LanguageSwitcher />
                    <div className="flex justify-center">
                        <img src={logoIcon} alt="AusspracheTrainer Logo" className="h-20 w-20" />
                    </div>

                    <div className="text-center space-y-1">
                        <h2 className="text-2xl text-gray-900">{t("auth.setPasswordTitle")}</h2>
                        <p className="text-sm text-gray-500">{t("auth.setPasswordSubtitle")}</p>
                    </div>

                    <form className="space-y-4" onSubmit={handleSetPassword}>
                        <div>
                            <label
                                htmlFor="new-password"
                                className="block text-sm font-medium text-gray-700 mb-1"
                            >
                                {t("auth.password")}{" "}
                                <span className="text-xs text-gray-500">
                                    (min. 8 {t("auth.characters")})
                                </span>
                            </label>
                            <div className="relative">
                                <input
                                    id="new-password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    autoFocus
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        setPasswordError("");
                                        setPasswordTooShort(false);
                                    }}
                                    required
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md p-1"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth={1.5}
                                            stroke="currentColor"
                                            className="size-4"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                                            />
                                        </svg>
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth={1.5}
                                            stroke="currentColor"
                                            className="size-4"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {passwordError && (
                                <p className="mt-1 text-sm text-error">{passwordError}</p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={settingPassword}
                            className="w-full bg-primary hover:bg-purple-500 text-white py-2.5 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {settingPassword && (
                                <svg
                                    className="animate-spin h-4 w-4 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                            )}
                            {t("auth.createPasswordButton")}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // ── Form step (role + email) ─────────────────────────────────────
    return (
        <div className="flex justify-center bg-white pt-8 sm:pt-16 px-2 sm:p-2">
            <div className="w-full max-w-md space-y-4 sm:space-y-6 p-4 sm:p-8 border border-gray-200 rounded-xl shadow-sm relative step-fade-in">
                <CloseButton />
                <LanguageSwitcher />
                <div className="flex justify-center">
                    <Link to="/">
                        <img
                            src={logoIcon}
                            alt="AusspracheTrainer Logo"
                            className="h-24 w-24"
                        />
                    </Link>
                </div>
                <h2 className="text-center text-2xl text-gray-900">{t("auth.createAccount")}</h2>
                <p className="text-center text-sm text-gray-500">{t("auth.registerDetails")}</p>

                {/* Display general form error */}
                {formError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {formError}
                    </div>
                )}

                <form className="space-y-4" onSubmit={handleSubmit}>
                    {/* Role selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("auth.roleQuestion")}
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            {roleOptions.map((option) => {
                                const selected = role === option.id;
                                return (
                                    <button
                                        type="button"
                                        key={option.id}
                                        onClick={() => {
                                            setRole(option.id);
                                            setRoleError(false);
                                        }}
                                        className={`group relative flex flex-col items-center text-center gap-2 rounded-xl border-2 p-4 transition-all duration-200
                                            ${
                                                selected
                                                    ? "border-primary bg-primary/5 shadow-sm scale-[1.02]"
                                                    : "border-gray-200 hover:border-primary/50 hover:bg-gray-50"
                                            }`}
                                    >
                                        {selected && (
                                            <span className="absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-white">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={3}
                                                    className="h-3 w-3"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="m4.5 12.75 6 6 9-13.5"
                                                    />
                                                </svg>
                                            </span>
                                        )}
                                        <span
                                            className={`flex h-12 w-12 items-center justify-center rounded-full transition-colors ${
                                                selected
                                                    ? "bg-primary text-white"
                                                    : "bg-gray-100 text-gray-500 group-hover:text-primary"
                                            }`}
                                        >
                                            {option.icon}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">
                                            {option.title}
                                        </span>
                                        <span className="text-xs text-gray-500 leading-tight">
                                            {option.subtitle}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {roleError && (
                            <p className="mt-1 text-sm text-error">{t("auth.roleRequired")}</p>
                        )}
                    </div>

                    {/* Teacher beta gate */}
                    {role === "teacher" && (
                        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3 step-fade-in">
                            <div className="flex items-start gap-2">
                                <span className="mt-0.5 inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                                    {t("auth.betaBadge")}
                                </span>
                            </div>
                            <p className="text-sm text-gray-600">{t("auth.teacherBetaNotice")}</p>
                            <div>
                                <label
                                    htmlFor="teacherCode"
                                    className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                    {t("auth.teacherCodeLabel")}
                                </label>
                                <input
                                    id="teacherCode"
                                    type="text"
                                    placeholder={t("auth.teacherCodePlaceholder")}
                                    className={`w-full px-4 py-2 border ${
                                        teacherCodeError ? "border-red-500" : "border-gray-300"
                                    } rounded-lg text-sm bg-white tracking-wide`}
                                    value={teacherCode}
                                    onChange={(e) => {
                                        setTeacherCode(e.target.value);
                                        setTeacherCodeError("");
                                    }}
                                />
                                {teacherCodeError && (
                                    <p className="mt-1 text-sm text-error">{teacherCodeError}</p>
                                )}
                            </div>
                            <p className="text-xs text-gray-500">
                                {t("auth.teacherCodeRequest")}{" "}
                                <a
                                    href={`mailto:${TEACHER_CONTACT_EMAIL}?subject=${encodeURIComponent(
                                        t("auth.teacherCodeMailSubject")
                                    )}`}
                                    className="text-primary hover:underline font-medium"
                                >
                                    {TEACHER_CONTACT_EMAIL}
                                </a>
                            </p>
                        </div>
                    )}

                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700 mb-1"
                        >
                            {t("auth.email")}
                        </label>
                        <input
                            id="email"
                            type="email"
                            placeholder={t("auth.emailPlaceholder")}
                            className={`w-full px-4 py-2 border ${
                                emailError ? "border-red-500" : "border-gray-300"
                            } rounded-lg text-sm`}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setEmailError("");
                                setEmailExistsError(false);
                            }}
                            required
                        />
                        {emailError && <p className="mt-1 text-sm text-error">{emailError}</p>}
                    </div>
                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="privacyPolicy"
                            className="w-4 h-4"
                            checked={privacyPolicyAccepted}
                            onChange={(e) => setPrivacyPolicyAccepted(e.target.checked)}
                            required
                        />
                        <label htmlFor="privacyPolicy" className="text-sm text-gray-700">
                            {t("auth.acceptPrivacyPolicy")}{" "}
                            <a href="/privacy" className="text-purple-500 hover:underline">
                                {t("auth.privacyPolicy")}
                            </a>
                        </label>
                    </div>
                    <button
                        disabled={submitButtonIsDisabled}
                        type="submit"
                        className="w-full bg-primary hover:bg-purple-500 text-white py-2 rounded-md disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {t("auth.register")}
                    </button>
                </form>
                <p className="text-center text-sm text-gray-500">
                    {t("auth.hasAccount")}{" "}
                    <a href="/login" className="text-purple-500 hover:underline">
                        {t("auth.login")}
                    </a>
                </p>
            </div>
        </div>
    );
};

export default Register;
